/**
 * Unit-system helper — metric ↔ imperial conversions used by cards, reports,
 * and 3D scale bars. Mirrors backend/localisation.py.
 */
import api from "@/lib/api";

const CACHE_KEY = "smin_units_v1";
let CACHE = null;

const FALLBACK = [
  { code: "metric", name: "Metric", length: "m", mass: "t", elevation: "m",
    volume: "m³", area: "m²", area_long: "ha", length_long: "km",
    mass_long: "t", mass_big: "Mt" },
  { code: "imperial", name: "Imperial (US)", length: "ft", mass: "st", elevation: "ft",
    volume: "yd³", area: "ft²", area_long: "ac", length_long: "mi",
    mass_long: "st", mass_big: "Mst" },
];

// Conversion factors — always FROM metric TO the target system.
const CONV = {
  metric:   { length: 1.0, mass: 1.0, elevation: 1.0, volume: 1.0, area: 1.0 },
  imperial: {
    length: 3.28084,      // m → ft
    length_long: 0.621371,// km → mi
    mass: 1.10231,        // tonnes → short-tons
    elevation: 3.28084,   // m → ft
    volume: 1.30795,      // m³ → yd³
    area: 10.7639,        // m² → ft²
    area_long: 2.47105,   // ha → ac
  },
};

export async function preloadUnitSystems() {
  if (CACHE) return CACHE;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) CACHE = JSON.parse(raw);
  } catch (err) {
    console.warn("[units] cache read failed:", err?.message);
  }
  try {
    const { data } = await api.get("/unit-systems");
    if (Array.isArray(data.unit_systems) && data.unit_systems.length) {
      CACHE = data.unit_systems;
      localStorage.setItem(CACHE_KEY, JSON.stringify(CACHE));
    }
  } catch (err) {
    console.warn("[units] fetch failed, using fallback:", err?.message);
    if (!CACHE) CACHE = FALLBACK;
  }
  return CACHE;
}

export function listUnitSystems() {
  return CACHE || FALLBACK;
}

export function getUnitSystem(code) {
  const list = listUnitSystems();
  return list.find((u) => u.code === (code || "metric")) || list[0];
}

/** Convert a metric quantity to the chosen system. `kind` picks the factor. */
export function convert(metricValue, kind, system) {
  const factors = CONV[system || "metric"] || CONV.metric;
  const factor = factors[kind] ?? 1.0;
  return Number(metricValue) * factor;
}

/**
 * Pretty-format a metric value with unit suffix.
 *   formatUnit(1234.56, 'length', 'imperial')  -> "4,051 ft"
 *   formatUnit(2_500_000, 'mass', 'metric', { compact: true }) -> "2.50 Mt"
 * Iter 70 · Feb 2026: mass no longer emits `kt` — values below 1 Mt fall
 * back to the base `t` label with 2 decimals so figures are always exact.
 */
export function formatUnit(metricValue, kind, system, opts = {}) {
  const u = getUnitSystem(system);
  const val = convert(metricValue, kind, system);
  const digits = opts.digits ?? (kind === "mass" ? 2 : (Math.abs(val) >= 1000 ? 0 : 2));
  const nf = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  });
  const unit = u[kind] || "";
  if (opts.compact && kind === "mass" && Math.abs(val) >= 1_000_000) {
    return `${(val / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${u.mass_big || "Mt"}`;
  }
  if (opts.compact && kind === "length" && Math.abs(val) >= 1_000) {
    return `${(val / 1_000).toFixed(2)} ${u.length_long || "km"}`;
  }
  return `${nf.format(val)} ${unit}`.trim();
}
